import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Lock, Loader2, Home, MailCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PORTALS = {
  admin: { eyebrow: "Restricted · Back Office", loginPath: "/admin/login", loginLabel: "Sign In" },
  borrower: { eyebrow: "Borrower Portal", loginPath: "/borrower", loginLabel: "Borrower Sign In" },
  broker: { eyebrow: "Broker / Agent Desk", loginPath: "/broker", loginLabel: "Broker Sign In" },
};

export default function ForgotPassword({ portal = "admin" }) {
  const cfg = PORTALS[portal] || PORTALS.admin;
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim(), origin: window.location.origin, portal });
      setSent(true);
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message || "Something went wrong.");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#ECECE4] flex items-center justify-center px-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase text-[#B8860B] font-bold mb-3">
            <Lock size={11} /> {cfg.eyebrow}
          </div>
          <h1 className="text-3xl font-serif tracking-tight">Reset Password</h1>
          <p className="text-[#8A8A8A] text-sm mt-1">We'll email you a secure reset link.</p>
          <Link to={cfg.loginPath} data-testid="forgot-back-login" className="inline-flex items-center gap-1.5 mt-4 text-[11px] uppercase tracking-[0.14em] text-[#8A8A8A] hover:text-[#ECECE4] transition-colors"><Home size={12} /> Back to {cfg.loginLabel}</Link>
        </div>

        {sent ? (
          <div className="bg-[#13130E] border border-[#2A2A22] rounded-md p-7 text-center" data-testid="forgot-sent">
            <MailCheck size={34} className="text-[#3DBB8E] mx-auto mb-3" />
            <p className="text-[#ECECE4] text-sm">If an account exists for <span className="text-[#E4C97A]">{email}</span>, a password-reset link is on its way. It's valid for 1 hour.</p>
            <p className="text-[#5C5C5C] text-[11px] mt-3">Check your inbox (and spam). The link opens a page to set a new password.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-[#13130E] border border-[#2A2A22] rounded-md p-7 shadow-2xl">
            <label className="text-[10px] uppercase tracking-[0.18em] text-[#8A8A8A] block mb-1.5">Email</label>
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" autoComplete="email" required
              className="w-full bg-[#0A0A0A] border border-[#2A2A22] rounded-sm px-3 py-2 text-[#ECECE4] focus:border-[#B8860B] focus:outline-none transition-colors"
              data-testid="forgot-email"
            />
            {err && (
              <div className="mt-3 p-2 rounded-sm bg-[#9E2A2B]/15 border border-[#9E2A2B]/40 text-[#FF8585] text-[12px]" data-testid="forgot-error">{err}</div>
            )}
            <button type="submit" disabled={busy}
              className="mt-5 w-full bg-[#B8860B] hover:bg-[#a0760a] disabled:opacity-50 text-[#0A0A0A] font-bold uppercase tracking-[0.14em] text-[12px] py-2.5 rounded-sm flex items-center justify-center gap-2"
              data-testid="forgot-submit">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : "Send Reset Link"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
